import React, { useEffect, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import app, { professorAuth } from "../../firebase";
import ResetPassword from '../admin/users/ResetPassword';

const auth = professorAuth;
const db = getFirestore(app);

export default function ProfessorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profName, setProfName] = useState("Professor");
  const [profEmail, setProfEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/academics");
        return;
      }

      setProfEmail(user.email || "");
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setProfName(data.name || "Professor");
          setDepartment(data.department || "");
        } else {
          const usersRef = collection(db, "users");
          const lookup = query(usersRef, where("email", "==", user.email));
          const result = await getDocs(lookup);
          if (!result.empty) {
            const data = result.docs[0].data();
            setProfName(data.name || "Professor");
            setDepartment(data.department || "");
          }
        }
      } catch (err) {
        console.error("Failed to load professor profile:", err);
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (!profEmail) return;

    const fetchSchedules = async () => {
      try {
        const schedulesRef = collection(db, "schedules");
        // Primary: query schedules where canonical `prof` (email) was saved
        const q = query(schedulesRef, where("prof", "==", profEmail));
        const snap = await getDocs(q);
        let scheduleRows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // If nothing matched, broaden client-side by fetching recent schedules and
        // matching against several possible professor identifiers stored in documents
        if (scheduleRows.length === 0) {
          const allSnap = await getDocs(schedulesRef);
          const lowerEmail = profEmail.toLowerCase();
          const lowerName = String(profName || "").toLowerCase();
          scheduleRows = allSnap.docs
            .map((doc) => ({ id: doc.id, ...doc.data() }))
            .filter((sched) => {
              const profFields = [sched.prof, sched.professorEmail, sched.professor, sched.professorId]
                .filter(Boolean)
                .map(v => String(v).toLowerCase());
              // match email anywhere
              if (profFields.some(f => f.includes(lowerEmail))) return true;
              // match by name if available
              if (String(sched.professor || "").toLowerCase().includes(lowerName)) return true;
              return false;
            });
        }

        setSchedules(scheduleRows);
      } catch (err) {
        console.error("Failed to load professor schedules:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [profEmail]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/academics");
  };

  const uniqueValues = (key) => Array.from(new Set(schedules.map((row) => row[key]).filter(Boolean))).sort();
  const schoolYears = uniqueValues("schoolYear");
  const departments = uniqueValues("department");
  const yearLevels = uniqueValues("yearLevel");
  const semesters = uniqueValues("semester");
  const sections = uniqueValues("section");
  const courses = Array.from(new Set(schedules.map((row) => row.courseId).filter(Boolean))).sort();

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      <div style={{ width: 220, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", minHeight: "100vh", boxShadow: "2px 0 8px #eee" }}>
        <div style={{ fontWeight: 700, fontSize: 22, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>
        <button onClick={() => navigate('/professor')} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, boxShadow: "0 2px 8px #e67e22aa" }}>Home</button>
        <button onClick={() => navigate('/professor/schedule')} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Schedule</button>
        <button onClick={() => navigate('/professor/grading')} style={{ background: "transparent", color: "#fff", border: "none", borderRadius: 6, padding: "14px 0", fontWeight: 600, fontSize: 17, cursor: "pointer", textAlign: "left", paddingLeft: 32, marginBottom: 8, marginRight: 12, transition: "background 0.2s" }}>Grading</button>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", justifyContent: "space-between" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: "#155c2b", fontWeight: 600 }}>Professor Dashboard</h1>
            <div style={{ color: "#4a5568", marginTop: 4 }}>{profName} • {department || "No department assigned"}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setShowResetModal(true)} style={{ background: '#fff', color: '#155c2b', border: '1px solid #dfe7e3', borderRadius: 10, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>Change Password</button>
            <button onClick={handleLogout} style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontWeight: 700, cursor: "pointer" }}>Log out</button>
            {showResetModal && <ResetPassword email={profEmail} onClose={() => setShowResetModal(false)} />}
          </div>
        </div>

        <div style={{ flex: 1, padding: 40, overflowY: "auto" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 20, marginBottom: 32 }}>
              <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
                <div style={{ color: "#155c2b", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Active schedule entries</div>
                <div style={{ fontSize: 38, fontWeight: 700, color: "#0f3f1e" }}>{schedules.length}</div>
              </div>
              <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
                <div style={{ color: "#155c2b", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Courses assigned</div>
                <div style={{ fontSize: 38, fontWeight: 700, color: "#0f3f1e" }}>{courses.length}</div>
              </div>
              <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
                <div style={{ color: "#155c2b", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Sections taught</div>
                <div style={{ fontSize: 38, fontWeight: 700, color: "#0f3f1e" }}>{sections.length}</div>
              </div>
            </div>

            <div style={{ marginBottom: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#155c2b", marginBottom: 12 }}>Quick filters</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
                  <div style={{ background: "#f8faf9", borderRadius: 12, padding: 16 }}><div style={{ color: "#4a5568", marginBottom: 8 }}>School years</div><div style={{ fontSize: 20, fontWeight: 700 }}>{schoolYears.length}</div></div>
                  <div style={{ background: "#f8faf9", borderRadius: 12, padding: 16 }}><div style={{ color: "#4a5568", marginBottom: 8 }}>Semesters</div><div style={{ fontSize: 20, fontWeight: 700 }}>{semesters.length}</div></div>
                  <div style={{ background: "#f8faf9", borderRadius: 12, padding: 16 }}><div style={{ color: "#4a5568", marginBottom: 8 }}>Departments</div><div style={{ fontSize: 20, fontWeight: 700 }}>{departments.length}</div></div>
                  <div style={{ background: "#f8faf9", borderRadius: 12, padding: 16 }}><div style={{ color: "#4a5568", marginBottom: 8 }}>Year levels</div><div style={{ fontSize: 20, fontWeight: 700 }}>{yearLevels.length}</div></div>
                </div>
              </div>
              <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#155c2b", marginBottom: 12 }}>Next actions</div>
                <button onClick={() => navigate('/professor/schedule')} style={{ width: "100%", padding: "14px 0", background: "#155c2b", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", marginBottom: 12 }}>View Schedule</button>
                <button onClick={() => navigate('/professor/grading')} style={{ width: "100%", padding: "14px 0", background: "#e67e22", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer" }}>Enter Grades</button>
              </div>
            </div>

            <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(22, 44, 32, 0.05)" }}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: "#155c2b" }}>Assigned Schedule Preview</div>
              {loading ? (
                <div style={{ color: "#718096" }}>Loading schedule details...</div>
              ) : schedules.length === 0 ? (
                <div style={{ color: "#718096" }}>No assigned schedules were found for your account.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f7faf7" }}>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>School Year</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Department</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Year Level</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Semester</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Section</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Course</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Day</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Time</th>
                        <th style={{ padding: 12, border: "1px solid #e5e7eb", textAlign: "left", color: "#334155", fontWeight: 700 }}>Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.slice(0, 6).map((row) => (
                        <tr key={row.id}>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.schoolYear || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.department || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.yearLevel || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.semester || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.section || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.courseId || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.day || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.time || "—"}</td>
                          <td style={{ padding: 12, border: "1px solid #e5e7eb" }}>{row.room || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
